import React, { useState } from "react";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../firebase";

// ── Design tokens ─────────────────────────────────────────────────────────────
const T = {
  cobalt900: "#081627",
  cobalt800: "#0B1F36",
  cobalt700: "#163655",
  cobalt600: "#1B3E5E",
  cobalt500: "#214B70",
  cobalt50:  "#E8EEF5",
  brass:     "#B68A35",
  brassAlpha:"rgba(182,138,53,0.14)",
  ink:       "#0A0D12",
  stone600:  "#3B424D",
  stone400:  "#8A92A0",
  stone200:  "#E1E4EA",
  stone100:  "#EEF0F4",
  stone50:   "#F7F8FA",
  white:     "#FFFFFF",
  rust700:   "#6E1F18",
  rust50:    "#F6E0DA",
};

const SERVICE_CHIPS = [
  "Valet & Vehicle Retrieval",
  "Guest Parking",
  "EV Charging",
  "Car Wash & Detailing",
  "Payments & Receipts",
  "Building Requests",
];

// ── Props (unchanged) ─────────────────────────────────────────────────────────
interface PortalLoginCardProps {
  email: string;
  password: string;
  onEmailChange: (email: string) => void;
  onPasswordChange: (password: string) => void;
  onLogin: (e: React.FormEvent<HTMLFormElement>) => void;
  loading?: boolean;
  submitting?: boolean;
  error?: string;
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function PortalLoginCard({
  email,
  password,
  onEmailChange,
  onPasswordChange,
  onLogin,
  loading = false,
  submitting = false,
  error,
}: PortalLoginCardProps) {
  const [resetMsg, setResetMsg]       = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  async function handleForgotPassword() {
    if (!email.trim()) {
      setResetMsg("Enter your email above, then click Forgot password.");
      return;
    }
    setResetLoading(true);
    try {
      await sendPasswordResetEmail(auth, email.trim());
    } catch (err: unknown) {
      const code = (err as { code?: string }).code ?? "";
      if (code === "auth/invalid-email") {
        setResetLoading(false);
        setResetMsg("That doesn't look like a valid email address.");
        return;
      }
      // anti-enumeration: treat user-not-found as success
    } finally {
      setResetLoading(false);
    }
    setResetMsg("If an account exists for that email, a reset link has been sent. Check your inbox.");
  }

  return (
    <>
      {/*
        position:fixed + inset:0 breaks out of #root's flex-column entirely,
        giving us true full-viewport ownership. CSS Grid splits hero/panel
        with explicit minmax so neither column collapses below a usable width.
      */}
      <style>{`
        .ps-root {
          position: fixed;
          inset: 0;
          display: grid;
          grid-template-columns: minmax(520px, 58fr) minmax(420px, 42fr);
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          color: ${T.ink};
          overflow: hidden;
        }

        /* ── Hero ── */
        .ps-hero {
          position: relative;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: flex-start;
          padding: 60px 72px;
        }
        .ps-hero-content {
          position: relative;
          z-index: 1;
          width: 100%;
          max-width: 520px;
        }

        /* ── Panel ── */
        .ps-panel {
          background: ${T.white};
          display: flex;
          flex-direction: column;
          justify-content: center;
          overflow-y: auto;
          padding: 48px 64px;
          border-left: 1px solid ${T.stone200};
        }
        .ps-panel-inner {
          width: 100%;
          max-width: 400px;
          margin: 0 auto;
        }

        /* ── Mobile: stack ── */
        @media (max-width: 960px) {
          .ps-root {
            position: static;
            display: flex;
            flex-direction: column;
            min-height: 100vh;
            overflow: visible;
          }
          .ps-hero {
            padding: 48px 32px 44px;
            justify-content: flex-start;
          }
          .ps-hero-content { max-width: 100%; }
          .ps-panel {
            flex: 1;
            padding: 40px 32px 56px;
            border-left: none;
            border-top: 1px solid ${T.stone200};
            justify-content: flex-start;
          }
          .ps-panel-inner { max-width: 100%; }
        }

        @media (max-width: 480px) {
          .ps-hero  { padding: 36px 20px 32px; }
          .ps-panel { padding: 32px 20px 48px; }
        }
      `}</style>

      <div className="ps-root">

        {/* ── LEFT HERO ─────────────────────────────────────────────────────── */}
        <div
          className="ps-hero"
          style={{
            background: [
              "radial-gradient(900px 700px at 70% -10%, #2D5B82 0%, transparent 55%)",
              "radial-gradient(700px 600px at 0% 100%, #14406A 0%, transparent 55%)",
              "linear-gradient(160deg, #0E2740 0%, #0B1F36 55%, #081627 100%)",
            ].join(", "),
          }}
        >
          {/* Brass glow */}
          <div style={{
            position: "absolute", inset: 0, pointerEvents: "none",
            background: "radial-gradient(640px 440px at 45% 20%, rgba(182,138,53,0.11) 0%, transparent 60%)",
          }} />
          {/* Subtle grid texture */}
          <div style={{
            position: "absolute", inset: 0, pointerEvents: "none", opacity: 0.38,
            backgroundImage: [
              "linear-gradient(rgba(255,255,255,0.022) 1px, transparent 1px)",
              "linear-gradient(90deg, rgba(255,255,255,0.022) 1px, transparent 1px)",
            ].join(", "),
            backgroundSize: "56px 56px",
            WebkitMaskImage: "radial-gradient(800px 600px at 48% 38%, #000 0%, transparent 72%)",
            maskImage:       "radial-gradient(800px 600px at 48% 38%, #000 0%, transparent 72%)",
          } as React.CSSProperties} />

          <div className="ps-hero-content">
            {/* Logo */}
            <img
              src="/logo/crastu.png"
              alt="Atrium"
              width={52}
              height={52}
              style={{
                borderRadius: "12px",
                marginBottom: "28px",
                display: "block",
                boxShadow: "0 8px 28px -6px rgba(0,0,0,0.55)",
              }}
            />

            {/* Eyebrow pill */}
            <span style={{
              display: "inline-block",
              fontSize: "11px", fontWeight: 700,
              letterSpacing: "0.18em", textTransform: "uppercase",
              color: T.brass, background: T.brassAlpha,
              border: "1px solid rgba(182,138,53,0.3)",
              borderRadius: "999px", padding: "4px 14px",
              marginBottom: "20px",
            }}>
              Atrium Resident Portal
            </span>

            {/* Headline — word-break:keep-all prevents single-word orphan lines */}
            <h1 style={{
              margin: "0 0 18px",
              fontSize: "clamp(26px, 2.8vw, 40px)",
              fontWeight: 700,
              letterSpacing: "-0.025em",
              lineHeight: 1.18,
              color: T.white,
              wordBreak: "keep-all",
              overflowWrap: "normal",
            }}>
              Your building services,<br />one tap away.
            </h1>

            {/* Body copy */}
            <p style={{
              margin: "0 0 36px",
              fontSize: "15px", lineHeight: 1.7,
              color: "rgba(255,255,255,0.6)",
              maxWidth: "440px",
            }}>
              Valet, guest parking, EV charging, car wash, payments, and building
              requests — all in one place for residents.
            </p>

            {/* Service chips */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "28px" }}>
              {SERVICE_CHIPS.map((chip) => (
                <span key={chip} style={{
                  fontSize: "12px", fontWeight: 500,
                  color: "rgba(255,255,255,0.76)",
                  background: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.14)",
                  borderRadius: "999px", padding: "5px 13px",
                  whiteSpace: "nowrap",
                }}>
                  {chip}
                </span>
              ))}
            </div>

            <p style={{
              margin: 0,
              fontSize: "11.5px", lineHeight: 1.55,
              color: "rgba(255,255,255,0.32)",
            }}>
              Available services depend on your building's enabled features.
            </p>
          </div>
        </div>

        {/* ── RIGHT AUTH PANEL ──────────────────────────────────────────────── */}
        <div className="ps-panel" id="portal-login-form">
          <div className="ps-panel-inner">

            {/* Branding */}
            <div style={{ marginBottom: "32px" }}>
              <div style={{
                fontSize: "11px", fontWeight: 700,
                letterSpacing: "0.16em", textTransform: "uppercase",
                color: T.brass, marginBottom: "10px",
              }}>
                Resident Portal
              </div>
              <div style={{
                fontSize: "22px", fontWeight: 700,
                letterSpacing: "-0.02em", color: T.ink,
              }}>
                Sign in
              </div>
              <div style={{ fontSize: "13.5px", color: T.stone400, marginTop: "6px" }}>
                Use your Atrium CMD building account
              </div>
            </div>

            {/* Error */}
            {error && (
              <div style={{
                display: "flex", alignItems: "flex-start", gap: "9px",
                padding: "11px 13px", borderRadius: "10px",
                fontSize: "12.5px", lineHeight: 1.45, marginBottom: "20px",
                background: T.rust50, color: T.rust700,
              }}>
                <AlertSVG />
                <span>{error}</span>
              </div>
            )}

            {/* Reset message */}
            {resetMsg && (
              <div style={{
                padding: "11px 13px", borderRadius: "10px",
                fontSize: "12.5px", lineHeight: 1.5, marginBottom: "20px",
                background: "#EAF3E8", color: "#1A4D29",
                border: "1px solid #BDD9B3",
              }}>
                {resetMsg}
              </div>
            )}

            {/* Form — auth behavior unchanged */}
            <form onSubmit={onLogin} noValidate>
              <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>

                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  <label htmlFor="portal-email" style={{ fontSize: "13px", fontWeight: 600, color: T.stone600 }}>
                    Email
                  </label>
                  <input
                    id="portal-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => onEmailChange(e.target.value)}
                    placeholder="you@building.com"
                    disabled={loading}
                    className="input-field"
                    style={{ height: "46px" }}
                  />
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <label htmlFor="portal-password" style={{ fontSize: "13px", fontWeight: 600, color: T.stone600 }}>
                      Password
                    </label>
                    <button
                      type="button"
                      onClick={handleForgotPassword}
                      disabled={resetLoading}
                      style={{
                        background: "none", border: "none", padding: 0,
                        fontSize: "12.5px", color: "#3B82F6",
                        cursor: "pointer", fontWeight: 500, lineHeight: 1,
                      }}
                    >
                      {resetLoading ? "Sending…" : "Forgot password?"}
                    </button>
                  </div>
                  <input
                    id="portal-password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => onPasswordChange(e.target.value)}
                    placeholder="••••••••"
                    disabled={loading}
                    className="input-field"
                    style={{ height: "46px" }}
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting || loading || !email || !password}
                  className="btn-primary"
                  style={{
                    width: "100%", height: "48px", marginTop: "2px",
                    background: submitting || loading || !email || !password ? undefined : T.cobalt500,
                    borderRadius: "10px", fontSize: "15px", fontWeight: 600,
                  }}
                >
                  {submitting || loading ? "Signing in…" : "Sign In"}
                </button>
              </div>
            </form>

            <div style={{
              marginTop: "28px", paddingTop: "24px",
              borderTop: `1px solid ${T.stone200}`,
              textAlign: "center",
            }}>
              <a
                href="mailto:hello@crastu.com"
                style={{ fontSize: "13px", color: T.stone400, textDecoration: "none" }}
              >
                Need help? Contact support
              </a>
            </div>

            <div style={{ marginTop: "24px", textAlign: "center" }}>
              <p style={{ margin: 0, fontSize: "11px", color: T.stone400, lineHeight: 1.6 }}>
                Services available depend on your building's configuration.<br />
                Not all features are active at every property.
              </p>
            </div>

          </div>
        </div>

      </div>
    </>
  );
}

// ── SVG icon ──────────────────────────────────────────────────────────────────

function AlertSVG() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: "1px" }}>
      <circle cx="12" cy="12" r="10"/>
      <line x1="12" y1="8" x2="12" y2="12"/>
      <line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
  );
}
